/**
 * Object Explorer listings for Azure Storage (spec §5.1). TanStack Query over
 * `azure-storage:list` with the pinned key shape from
 * `storage-explorer/services/storageListingKeys.ts` — the SAME key the grid
 * uses, so tree and grid share one cache shape and one invalidation prefix.
 * Never `objectsService` / `databasesService` / `executeQueryV2` (ruling F1).
 * Generation-fenced through the caller's `isCurrent` (the explorer's
 * `connectionGenerations`), never a counter of its own.
 */
import { QueryClient } from '@tanstack/react-query';
import type { MessageTreeNode, PinResolution, StorageActionNode, StorageServiceFolderNode } from '../../explorer/models/nodeTypes';
import type { DatabaseExplorerNode } from '../../explorer/models/nodeTypes';
import { createStorageResourceNode } from '../../explorer/models/azureStorageNodeFactories';
import type { StorageItem, StorageListQuery, StorageOperationError, StorageResourceRef, StorageService } from '../../../types/azureStorage';
import { listStoragePage, newStorageRequestId, toStorageOperationError } from '../../workspace/storage-explorer/services/azureStorageApi';
import { STORAGE_QUERY_ROOT, storageListingQueryKey, storageQueuesQueryKey, storageSharesQueryKey } from '../../workspace/storage-explorer/services/storageListingKeys';
import { serviceFolderActionRows, storageActionNodeId } from './storageTreeExtensions';
import { namesRequiredDetail, namesRequiredRowId } from './storageFileFolderState';
import { getStorageAccountInfoSnapshot } from '../store/storageAccountInfoStore';
import { withHnsBadges } from '../../workspace/storage-explorer/hns/hnsBadges';

export const STORAGE_CONTAINERS_QUERY: StorageListQuery = { kind: 'containers' };
export const STORAGE_TABLES_QUERY: StorageListQuery = { kind: 'tables' };
const BLOB_SERVICE_REF: StorageResourceRef = { kind: 'service', service: 'blob' };
const QUEUE_SERVICE_REF: StorageResourceRef = { kind: 'service', service: 'queue' };
const STORAGE_QUEUES_QUERY: StorageListQuery = { kind: 'queues', includeCounts: true };
const TABLE_SERVICE_REF: StorageResourceRef = { kind: 'service', service: 'table' };
const FILE_SERVICE_REF: StorageResourceRef = { kind: 'service', service: 'file' };
const STORAGE_SHARES_QUERY: StorageListQuery = { kind: 'shares' };
/** S7 §3.2: the File Shares folder of an Entra connection with no share listing and no named shares keeps only this row. */
const OPEN_SHARE_BY_NAME_ACTION = 's7-open-share-by-name';
const CONTAINERS_PAGE_SIZE = 5000;
/** A tree listing is fetched to completion; this bounds a runaway continuation (250k containers). */
const MAX_TREE_PAGES = 50;

/** One complete (or bounded) listing, the cache value every tree consumer reads. */
export type StorageListingSnapshot = {
  items: StorageItem[];
  completeness: 'page' | 'complete';
  connectionGeneration: number;
};

let fallbackClient: QueryClient | undefined;
/**
 * The explorer hooks receive the app's QueryClient from `DatabaseExplorer`
 * (so tree listings and storage pins share one cache with the grid). Callers
 * rendered without a provider (hook unit tests of the SQL branches) get a
 * private client instead of a thrown `No QueryClient set`.
 */
export function getStorageFallbackQueryClient(): QueryClient {
  fallbackClient ??= new QueryClient({ defaultOptions: { queries: { retry: false } } });
  return fallbackClient;
}

export function storageContainersQueryKey(connectionId: string): readonly unknown[] {
  return storageListingQueryKey(connectionId, BLOB_SERVICE_REF, STORAGE_CONTAINERS_QUERY);
}

export function storageTablesQueryKey(connectionId: string): readonly unknown[] {
  return storageListingQueryKey(connectionId, TABLE_SERVICE_REF, STORAGE_TABLES_QUERY);
}

/** True for any key the storage listings own; the connection id sits at index 1 (the explorer's `database-objects` keys keep it at 2). */
export function isStorageListingQueryKey(key: readonly unknown[]): key is readonly [typeof STORAGE_QUERY_ROOT, string, ...unknown[]] {
  return key[0] === STORAGE_QUERY_ROOT && typeof key[1] === 'string';
}

export async function fetchAllStoragePages(args: {
  connectionId: string;
  resource: StorageResourceRef;
  query: StorageListQuery;
  pageSize?: number;
  isCurrent?: () => boolean;
  /** An explicit Refresh: the FIRST page bypasses main's 60 s cache (review finding 8 / M3). */
  bypassCache?: boolean;
}): Promise<StorageListingSnapshot> {
  const { connectionId, resource, query, pageSize = CONTAINERS_PAGE_SIZE, isCurrent, bypassCache } = args;
  const items: StorageItem[] = [];
  let cursorHandle: string | undefined;
  let generation = 0;
  for (let pageIndex = 0; pageIndex < MAX_TREE_PAGES; pageIndex += 1) {
    if (isCurrent && !isCurrent()) {
      const cancelled: StorageOperationError = { category: 'cancelled', retryable: false, message: 'Listing cancelled by a reconnect' };
      throw cancelled;
    }
    const page = await listStoragePage({
      connectionId,
      resource,
      query,
      pageSize,
      cursorHandle,
      requestId: newStorageRequestId(),
      timeoutMs: 30_000,
      ...(bypassCache && pageIndex === 0 ? { bypassCache: true } : {}),
    });
    if (pageIndex === 0) generation = page.connectionGeneration;
    else if (page.connectionGeneration !== generation) {
      const stale: StorageOperationError = { category: 'cancelled', retryable: true, message: 'Listing restarted: the connection was rebuilt' };
      throw stale;
    }
    items.push(...page.items);
    if (!page.hasMore || !page.nextCursorHandle) return { items, completeness: 'complete', connectionGeneration: generation };
    cursorHandle = page.nextCursorHandle;
  }
  return { items, completeness: 'page', connectionGeneration: generation };
}

type ServiceListingArgs = { queryClient: QueryClient; connectionId: string; isCurrent?: () => boolean; forceReload?: boolean };

/** One service folder's listing through the shared cache: a forced reload drops the entry and bypasses main's 60 s cache. */
function cachedServiceListing(
  args: ServiceListingArgs,
  queryKey: readonly unknown[],
  fetch: (bypassCache: boolean | undefined) => Promise<StorageListingSnapshot>,
): Promise<StorageListingSnapshot> {
  const { queryClient, forceReload } = args;
  if (forceReload) queryClient.removeQueries({ queryKey, exact: true });
  return queryClient.fetchQuery({ queryKey, queryFn: () => fetch(forceReload), staleTime: Infinity, retry: false });
}

export async function listStorageContainers(args: ServiceListingArgs): Promise<StorageListingSnapshot> {
  const { connectionId, isCurrent } = args;
  return cachedServiceListing(args, storageContainersQueryKey(connectionId), (bypassCache) =>
    fetchAllStoragePages({ connectionId, resource: BLOB_SERVICE_REF, query: STORAGE_CONTAINERS_QUERY, isCurrent, bypassCache }));
}

export async function listStorageQueues(args: ServiceListingArgs): Promise<StorageListingSnapshot> {
  const { connectionId, isCurrent } = args;
  return cachedServiceListing(args, storageQueuesQueryKey(connectionId), (bypassCache) =>
    fetchAllStoragePages({ connectionId, resource: QUEUE_SERVICE_REF, query: STORAGE_QUEUES_QUERY, isCurrent, bypassCache }));
}

export async function listStorageTables(args: ServiceListingArgs): Promise<StorageListingSnapshot> {
  const { connectionId, isCurrent } = args;
  return cachedServiceListing(args, storageTablesQueryKey(connectionId), async (bypassCache) => {
    const snapshot = await fetchAllStoragePages({ connectionId, resource: TABLE_SERVICE_REF, query: STORAGE_TABLES_QUERY, pageSize: 1000, isCurrent, bypassCache });
    const tables = snapshot.items.filter((item): item is Extract<StorageItem, { kind: 'table' }> => item.kind === 'table');
    return { ...snapshot, items: tables.sort((a, b) => a.name.localeCompare(b.name)) };
  });
}

/** S7: the File Shares folder (FileREST List Shares, or the named shares of an Entra connection — main picks). */
export async function listStorageShares(args: ServiceListingArgs): Promise<StorageListingSnapshot> {
  const { connectionId, isCurrent } = args;
  return cachedServiceListing(args, storageSharesQueryKey(connectionId), (bypassCache) =>
    fetchAllStoragePages({ connectionId, resource: FILE_SERVICE_REF, query: STORAGE_SHARES_QUERY, isCurrent, bypassCache }));
}

type ServiceLister = typeof listStorageContainers;
const SERVICE_LISTERS: Partial<Record<StorageService, ServiceLister>> = {
  blob: listStorageContainers,
  queue: listStorageQueues, // S5
  table: listStorageTables, // S4
  file: listStorageShares, // S7
};
/** Folder nouns: the loading row, the empty row. */
const FOLDER_NOUNS: Record<StorageService, { plural: string; empty: string }> = {
  blob: { plural: 'containers', empty: 'containers' },
  queue: { plural: 'queues', empty: 'queues' },
  table: { plural: 'tables', empty: 'tables' },
  file: { plural: 'shares', empty: 'file shares' },
};

function storageMessageNode(id: string, parentId: string, connectionId: string, name: string, kind: 'message' | 'error', error?: string): MessageTreeNode {
  return {
    id,
    parentId,
    name,
    type: kind,
    canHaveChildren: false,
    children: [],
    isExpanded: false,
    ...(kind === 'message' ? { icon: 'message' } : {}),
    metadata: { connectionId, ...(error ? { error } : {}) },
  };
}

/** Spec §5.3 error rows: the typed message plus the fix chip sentence, no raw body, no SAS. */
export function storageErrorRowLabel(error: StorageOperationError): string {
  const fix = error.fix;
  const chip =
    fix?.kind === 'needs-role' ? `needs ${fix.role}` :
    fix?.kind === 'edit-credentials' ? 'Edit credentials' :
    fix?.kind === 'start-emulator' ? 'Start Azurite and Refresh' :
    fix?.kind === 'check-network' ? 'Check the network and Refresh' :
    undefined;
  return chip ? `${error.message} · ${chip}` : error.message;
}

export async function loadStorageServiceFolder(args: {
  node: StorageServiceFolderNode;
  queryClient: QueryClient;
  updateNode: (nodeId: string, updates: Partial<DatabaseExplorerNode>) => void;
  isCurrent?: () => boolean;
  forceReload?: boolean;
  /**
   * A background re-list (a storage mutation elsewhere — `useStorageMutationSync`): a folder that already shows its
   * rows keeps them until the new list arrives, which then replaces them by id, so no row re-mounts (an open context
   * menu on a row stays open; A+B merge, round A's flake). An explicit Refresh still clears first, as on every engine.
   * Why storage only: no SQL folder re-lists in the background on its own — a SQL tree folder re-lists on Refresh (and
   * after a table clone, through the same Refresh) — while every storage write, from the UI, MCP or another tab, emits
   * `azure-storage:mutated` and re-lists its service folder.
   */
  keepRows?: boolean;
}): Promise<void> {
  const { node, queryClient, updateNode, isCurrent, forceReload = false, keepRows = false } = args;
  const { connectionId, service } = node.metadata;
  if (!forceReload && node.children && node.children.length > 0) {
    const first = node.children[0];
    if (first && first.type !== 'message' && first.type !== 'error' && first.type !== 'storage-action') return;
  }
  const list = SERVICE_LISTERS[service];
  if (!list) {
    updateNode(node.id, { children: [storageMessageNode(`${node.id}-later`, node.id, connectionId, 'Arrives with a later release', 'message')], isLoading: false });
    return;
  }
  const { plural, empty: emptyNoun } = FOLDER_NOUNS[service];
  // An action row names a command, not the folder: it carries the folder's identity only, never its badges
  // (the probe's "not verified" labels the service folder — S5 review F11).
  const { engine, resource } = node.metadata;
  const actionRows: StorageActionNode[] = serviceFolderActionRows(service).map((action) => ({
    id: storageActionNodeId(node, action.id),
    parentId: node.id,
    name: action.label,
    type: 'storage-action',
    canHaveChildren: false,
    children: [],
    isExpanded: false,
    metadata: { connectionId, engine, service, resource, actionId: action.id },
  }));
  // S7 §3.2: nothing to list (main answers an empty page) — the probe's sentence and the one way forward.
  const namesRequired = (): boolean => {
    const detail = service === 'file' ? namesRequiredDetail(connectionId) : undefined;
    if (detail === undefined) return false;
    const row = storageMessageNode(namesRequiredRowId(node.id), node.id, connectionId, detail, 'message');
    updateNode(node.id, { children: [row, ...actionRows.filter((action) => action.metadata.actionId === OPEN_SHARE_BY_NAME_ACTION)], isLoading: false });
    return true;
  };
  if (namesRequired()) return;
  const showsRows = (node.children ?? []).some((child) => child.type !== 'message' && child.type !== 'error' && child.type !== 'storage-action');
  if (!(keepRows && showsRows)) {
    updateNode(node.id, { children: [storageMessageNode(`${node.id}-loading`, node.id, connectionId, `Loading ${plural}...`, 'message')], isLoading: true });
  }
  try {
    const snapshot = await list({ queryClient, connectionId, isCurrent, forceReload });
    if (isCurrent && !isCurrent()) return;
    if (namesRequired()) return;   // review D F8: the account-info answer arrived during the listing — it wins over its empty page
    const children = withHnsBadges(snapshot.items
      .map((item) => createStorageResourceNode({ connectionId, parentId: node.id, item }))
      .filter((child): child is NonNullable<typeof child> => child !== null), getStorageAccountInfoSnapshot(connectionId)?.hns); // S8
    const empty = storageMessageNode(`${node.id}-empty`, node.id, connectionId, `No ${emptyNoun} yet`, 'message');
    if (service === 'queue') (empty.metadata as { action?: string }).action = 'create-queue'; // S5: the empty row also creates
    updateNode(node.id, {
      children: children.length > 0
        ? [...children, ...actionRows]
        : [empty, ...actionRows],
      isLoading: false,
    });
  } catch (raw) {
    const error = toStorageOperationError(raw);
    if (error.category === 'cancelled') return;
    updateNode(node.id, { children: [storageMessageNode(`${node.id}-error`, node.id, connectionId, `Error: ${storageErrorRowLabel(error)}`, 'error', error.message)], isLoading: false });
  }
}

/**
 * Pin resolution from the shared listing cache (brief default): a found item
 * resolves the pin; absence proves staleness only in a COMPLETE listing;
 * a partial page or an absent cache is `unresolved`. Tables / queues / shares
 * resolve from their own listings (S4 / S5 / S7); a directory / file pin by its share.
 */
export function resolveStoragePin(ref: StorageResourceRef, queryClient: QueryClient, connectionId: string): PinResolution {
  if (ref.kind === 'queue') {
    const snapshot = queryClient.getQueryData<StorageListingSnapshot>(storageQueuesQueryKey(connectionId));
    if (!snapshot) return 'unresolved';
    if (snapshot.items.some((item) => item.kind === 'queue' && item.name === ref.queue)) return 'resolved';
    return snapshot.completeness === 'complete' ? 'stale' : 'unresolved';
  }
  if (ref.kind === 'table' || ref.kind === 'entity') { // S4
    const snapshot = queryClient.getQueryData<StorageListingSnapshot>(storageTablesQueryKey(connectionId));
    if (!snapshot) return 'unresolved';
    if (snapshot.items.some((item) => item.kind === 'table' && item.name === ref.table)) return 'resolved';
    return snapshot.completeness === 'complete' ? 'stale' : 'unresolved';
  }
  if (ref.kind === 'share' || ref.kind === 'directory' || ref.kind === 'file') { // S7: resolved by its share, like a blob by its container
    const snapshot = queryClient.getQueryData<StorageListingSnapshot>(storageSharesQueryKey(connectionId));
    if (!snapshot) return 'unresolved';
    if (snapshot.items.some((item) => item.kind === 'share' && item.name === ref.share)) return 'resolved';
    return snapshot.completeness === 'complete' ? 'stale' : 'unresolved';
  }
  if (ref.kind !== 'container' && ref.kind !== 'blobPrefix' && ref.kind !== 'blob' && ref.kind !== 'hnsPath') return 'unresolved';
  const snapshot = queryClient.getQueryData<StorageListingSnapshot>(storageContainersQueryKey(connectionId));
  if (!snapshot) return 'unresolved';
  const container = ref.kind === 'hnsPath' ? ref.fileSystem : ref.container; // S8: a Data Lake pin resolves through its file system
  const found = snapshot.items.some((item) => item.kind === 'container' && item.name === container);
  if (found) return 'resolved';
  return snapshot.completeness === 'complete' ? 'stale' : 'unresolved';
}
